import type React from "react";
import { Link } from "react-router-dom";
import { Download, FileText } from "lucide-react";
import productsData from "../data/products.json";

interface Product {
  id: string;
  name: string;
  category: string;
  subcategory?: string;
  productType?: string;
  datasheet?: string;
  active?: boolean;
}

interface ReplacementGroup {
  title: string;
  referenceFamilies: string[];
  productIds: string[];
}

const isLocalPds = (href?: string) =>
  Boolean(href && href.startsWith("/pdf/pds/") && href.toLowerCase().endsWith(".pdf"));

const replacementGroups: ReplacementGroup[] = [
  {
    title: "Hydraulic Oil Replacements",
    referenceFamilies: [
      "Mobil DTE",
      "Mobil DTE 10 Excel",
      "Mobil DTE Ultra",
      "Shell Tellus",
      "Chevron AW Hydraulic Oil",
      "Chevron Rando",
      "Texaco Rando",
      "Exxon/Mobil Nuto",
    ],
    productIds: [
      "l-hm-anti-wear-hydraulic-oil",
      "l-hm-classic-anti-wear-hydraulic-oil",
      "l-hm-high-pressure-ashless-anti-wear-hydraulic-oil",
      "l-hv-low-temperature-anti-wear-hydraulic-oil",
      "l-hs-ultra-low-temperature-hydraulic-oil",
      "ester-based-fire-resistant-hydraulic-fluid",
      "hfc-water-glycol-fire-resistant-hydraulic-fluid",
    ],
  },
  {
    title: "Industrial Gear Oil Replacements",
    referenceFamilies: [
      "Mobilgear 600 XP",
      "Mobil SHC Gear",
      "Shell Omala",
      "Chevron Meropa",
      "Texaco Meropa",
    ],
    productIds: [
      "l-ckc-medium-duty-industrial-gear-oil",
      "l-ckd-heavy-duty-industrial-gear-oil",
      "ap-industrial-gear-oil",
      "ap-s-industrial-gear-oil",
      "4406-synthetic-hd-industrial-gear-oil",
      "4408-fully-synthetic-heavy-duty-industrial-gear-oil",
      "worm-gear-oil-cke-and-ckep",
      "heavy-duty-open-gear-oil",
    ],
  },
  {
    title: "Turbine Oil Replacements",
    referenceFamilies: ["Mobil DTE Turbine Oil", "Shell Turbo", "Chevron GST"],
    productIds: [
      "l-tsa-turbine-oil",
      "l-tsa-classic-turbine-oil",
      "l-tse-turbine-oil",
      "l-tsa-lf-long-service-life-turbine-oil",
      "l-tse-lf-long-service-life-extreme-pressure",
      "gas-turbine-oil",
      "tgf-m-32-extreme-pressure-gas-turbine-oil",
      "tgsb-m-turbine-oil",
      "anti-ammonia-turbine-oil",
    ],
  },
  {
    title: "Compressor Oil Replacements",
    referenceFamilies: ["Mobil Rarus", "Shell Corena", "Chevron Compressor Oil"],
    productIds: [
      "4502-synthetic-compressor-oil",
      "4506-synthetic-compressor-oil",
      "4524-synthetic-refrigeration-compressor-oil",
    ],
  },
  {
    title: "Heat Transfer Oil Replacements",
    referenceFamilies: ["Mobiltherm", "Shell Heat Transfer Oil", "Chevron Heat Transfer Oil"],
    productIds: [
      "l-qb-300-heat-transfer-oil",
      "l-qb-300-synthetic-heat-transfer-oil",
      "l-qc-320-heat-transfer-oil",
      "l-qd-340-synthetic-heat-transfer-oil",
      "l-qd-400-synthetic-heat-transfer-oil",
    ],
  },
  {
    title: "Slideway / Machine Tool Oil Replacements",
    referenceFamilies: ["Mobil Vactra", "Mobil Vacuoline", "Shell Tonna"],
    productIds: ["l-g-slideway-oil", "l-hg-slideway-hydraulic-oil"],
  },
  {
    title: "Rock Drill / Air Tool Oil Replacements",
    referenceFamilies: ["Mobil Almo", "Shell Torcula"],
    productIds: ["rock-drill-oil"],
  },
  {
    title: "Tractor / Transmission Fluid Replacements",
    referenceFamilies: ["Mobilfluid 424", "Shell Spirax", "Chevron 1000 THF / driveline fluids"],
    productIds: [
      "stou-tractor-oil",
      "sinopec-hydraulic-transmission-fluid",
      "hydraulic-transmission-multifunctional-fluid",
      "to-4-heavy-duty-transmission-fluid",
      "final-drive-and-axle-oil",
      "hd-final-drive-460",
      "automatic-transmission-fluid-iii-ld",
      "automatic-transmission-fluid-iii-h",
      "manual-transmission-fluid-for-commercial-vehicles",
      "sinopec-mtf-75w-90-fully-synthetic-transmission-gear-oil",
      "sinopec-mtf-p-ii-manual-transmission-fluid-for-passenger-vehicles",
    ],
  },
  {
    title: "Grease Replacements",
    referenceFamilies: ["Mobilith SHC", "Mobilgrease", "Shell Gadus", "Chevron Delo Grease / Ulti-Plex"],
    productIds: [
      "multipurpose-lithium-grease",
      "red-drawing-extreme-pressure-lithium-grease",
      "lithium-complex-grease",
      "extreme-pressure-lithium-complex-grease",
      "lithium-base-with-molybdenum-disulfide-grease",
      "mos2-lithium-complex-grease-7413",
      "heavy-duty-lc-mining-grease",
      "premium-mining-grease",
      "general-open-gear-grease",
      "high-temperature-grease-for-food-machines",
      "golden-grease",
      "premium-golden-grease",
      "ep-golden-grease",
      "crystal-grease",
      "mp-grease",
      "hp-r-grease",
      "hpxt-grease",
      "grk-a-grease",
      "blc-a-grease",
      "blc-d-grease",
      "roller-bearing-grease",
      "sugar-mill-grease",
      "jz-ep-a-polyurea-grease",
      "mcl-o-high-temperature-grease",
      "cable-protection-grease-7417b",
      "extreme-pressure-high-temperature-grease-7019-1",
      "multi-purpose-aviation-grease-7008",
    ],
  },
];

const EquivalentGuidePage: React.FC = () => {
  const activeProducts = (productsData as Product[]).filter((product) => product.active !== false);
  const productsById = new Map(activeProducts.map((product) => [product.id, product]));
  const groupsWithProducts = replacementGroups
    .map((group) => ({
      ...group,
      products: group.productIds
        .map((id) => productsById.get(id))
        .filter((product): product is Product => Boolean(product && isLocalPds(product.datasheet))),
    }))
    .filter((group) => group.products.length > 0);

  return (
    <div>
      <section className="relative bg-red-600 text-white">
        <div className="container mx-auto px-4 py-12 relative z-10">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Equivalent Guide</h1>
            <p className="text-xl">
              Use this guide as a starting point for identifying Sinopec lubricant options by product family, viscosity grade, and application. Contact PES to confirm the correct product match for your equipment and operating conditions.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-10">
            <p className="text-gray-700">
              Cross-reference information is for general guidance only. Final product selection should be confirmed against equipment requirements, OEM specifications, operating conditions, and the applicable PDS/SDS.
            </p>
          </div>

          <div className="space-y-8">
            {groupsWithProducts.map((group) => (
              <div key={group.title} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="p-5 border-b border-gray-200">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">{group.title}</h2>
                  <div className="flex flex-wrap gap-2">
                    {group.referenceFamilies.map((family) => (
                      <span key={family} className="bg-gray-100 text-gray-700 text-xs font-medium px-2 py-1 rounded-md">
                        {family}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full table-fixed text-left">
                    <colgroup>
                      <col className="w-1/2" />
                      <col className="w-1/3" />
                      <col className="w-1/6" />
                    </colgroup>
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 font-semibold text-gray-900">PES / Sinopec Product</th>
                        <th className="px-4 py-3 font-semibold text-gray-900">Product Family</th>
                        <th className="px-4 py-3 font-semibold text-gray-900">PDS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {group.products.map((product) => (
                        <tr key={product.id} className="border-t border-gray-200">
                          <td className="px-4 py-4">
                            <Link to={`/products/${product.id}`} className="font-medium text-gray-900 hover:text-red-600">
                              {product.name}
                            </Link>
                            <div className="text-sm text-gray-500">{product.category}</div>
                          </td>
                          <td className="px-4 py-4 text-gray-700">
                            {product.subcategory}
                            {product.productType && (
                              <div className="text-sm text-gray-500">{product.productType}</div>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <a
                              href={product.datasheet}
                              download
                              className="inline-flex items-center text-red-600 hover:text-red-700 font-medium text-sm"
                            >
                              <Download size={16} className="mr-2" />
                              Download PDS
                            </a>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-gray-50 p-6 rounded-lg border border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold mb-1">Need help with a product cross-reference?</h2>
              <p className="text-gray-600">
                Send PES the current product name, viscosity grade, application, equipment type, and required documents.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-6 rounded-md transition-colors"
            >
              <FileText size={18} className="mr-2" />
              Ask for Product Matching
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EquivalentGuidePage;
